import React from 'react';
import { TrendingUp, TrendingDown, BarChart3, Percent } from 'lucide-react';
import { Municipality, getFeesForYear, filterFees, getYearlyTotal, DEFAULT_ENABLED_CATEGORIES } from '@/lib/data';
import { getYearRange, getYearCount } from '@/config/years';

interface StatsWidgetProps {
  municipalities: Municipality[];
  year: number;
  show: ('highest' | 'lowest' | 'average' | 'increase')[];
  enabledCategories?: string[];
  dark?: boolean;
}

export const StatsWidget: React.FC<StatsWidgetProps> = ({ municipalities, year, show, enabledCategories = DEFAULT_ENABLED_CATEGORIES, dark }) => {
  const getTotal = (m: Municipality) =>
    filterFees(getFeesForYear(m, year), enabledCategories).reduce((s, f) => s + f.amount, 0);

  const withTotals = municipalities
    .map(m => ({ m, total: getTotal(m) }))
    .filter(x => x.total > 0);

  const highest = [...withTotals].sort((a, b) => b.total - a.total)[0];
  const lowest = [...withTotals].sort((a, b) => a.total - b.total)[0];
  const avgTotal = withTotals.length > 0
    ? withTotals.reduce((s, x) => s + x.total, 0) / withTotals.length
    : 0;

  let avgIncrease = 0;
  municipalities.forEach(m => {
    const h = m.historicalData;
    if (h?.length >= 2) {
      const oldestTotal = getYearlyTotal(h[0], enabledCategories);
      if (oldestTotal > 0) {
        avgIncrease += ((getYearlyTotal(h[h.length - 1], enabledCategories) - oldestTotal) / oldestTotal) * 100;
      }
    }
  });
  const count = municipalities.filter(m => m.historicalData?.length >= 2).length;
  avgIncrease = count > 0 ? avgIncrease / count : 0;

  const increaseColorCls = avgIncrease >= 0
    ? (dark ? 'text-amber-400' : 'text-amber-600')
    : (dark ? 'text-slate-400' : 'text-slate-600');

  return (
    <div className="space-y-3">
      {show.includes('highest') && highest && (
        <div className={`flex items-center gap-3 p-3 rounded-lg ${dark ? 'bg-slate-800/50' : 'bg-gray-50 border border-gray-100'}`}>
          <div className={`rounded-full p-2 shrink-0 ${dark ? 'bg-slate-700' : 'bg-white'}`}>
            <TrendingUp className={`h-4 w-4 ${dark ? 'text-red-400' : 'text-red-500'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] uppercase tracking-wider ${dark ? 'text-slate-400' : 'text-warmGray'}`}>Høyeste</p>
            <p className={`text-sm font-medium truncate ${dark ? 'text-slate-200' : 'text-deepCharcoal'}`}>{highest.m.name}</p>
          </div>
          <p className={`text-sm font-semibold tabular-nums shrink-0 ${dark ? 'text-slate-300' : 'text-nordicBlue'}`}>
            {highest.total.toLocaleString('nb-NO')} kr
          </p>
        </div>
      )}
      {show.includes('lowest') && lowest && (
        <div className={`flex items-center gap-3 p-3 rounded-lg ${dark ? 'bg-slate-800/50' : 'bg-gray-50 border border-gray-100'}`}>
          <div className={`rounded-full p-2 shrink-0 ${dark ? 'bg-slate-700' : 'bg-white'}`}>
            <TrendingDown className={`h-4 w-4 ${dark ? 'text-slate-400' : 'text-slate-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] uppercase tracking-wider ${dark ? 'text-slate-400' : 'text-warmGray'}`}>Laveste</p>
            <p className={`text-sm font-medium truncate ${dark ? 'text-slate-200' : 'text-deepCharcoal'}`}>{lowest.m.name}</p>
          </div>
          <p className={`text-sm font-semibold tabular-nums shrink-0 ${dark ? 'text-slate-300' : 'text-nordicBlue'}`}>
            {lowest.total.toLocaleString('nb-NO')} kr
          </p>
        </div>
      )}
      {show.includes('average') && (
        <div className={`flex items-center gap-3 p-3 rounded-lg ${dark ? 'bg-slate-800/50' : 'bg-gray-50 border border-gray-100'}`}>
          <div className={`rounded-full p-2 shrink-0 ${dark ? 'bg-slate-700' : 'bg-white'}`}>
            <BarChart3 className={`h-4 w-4 ${dark ? 'text-blue-400' : 'text-nordicBlue'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] uppercase tracking-wider ${dark ? 'text-slate-400' : 'text-warmGray'}`}>Gj.snitt</p>
            <p className={`text-xs ${dark ? 'text-slate-400' : 'text-warmGray'}`}>
              {withTotals.length} kommuner
            </p>
          </div>
          <p className={`text-sm font-semibold tabular-nums shrink-0 ${dark ? 'text-slate-300' : 'text-nordicBlue'}`}>
            {avgTotal.toFixed(0)} kr
          </p>
        </div>
      )}
      {show.includes('increase') && (
        <div className={`flex items-center gap-3 p-3 rounded-lg ${dark ? 'bg-slate-800/50' : 'bg-gray-50 border border-gray-100'}`}>
          <div className={`rounded-full p-2 shrink-0 ${dark ? 'bg-slate-700' : 'bg-white'}`}>
            <Percent className={`h-4 w-4 ${dark ? 'text-amber-400' : 'text-amber-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] uppercase tracking-wider ${dark ? 'text-slate-400' : 'text-warmGray'}`}>Økning</p>
            <p className={`text-xs ${dark ? 'text-slate-400' : 'text-warmGray'}`}>
              Gj.snitt {getYearRange()}
            </p>
          </div>
          <p className={`text-sm font-semibold tabular-nums shrink-0 ${increaseColorCls}`}>
            {avgIncrease >= 0 ? '+' : ''}{avgIncrease.toFixed(1)}%
          </p>
        </div>
      )}
    </div>
  );
};
